import type { CategoryStat } from "../../types";

interface DonutChartProps {
  categories: CategoryStat[];
}

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

export default function DonutChart({ categories }: DonutChartProps) {
  const r = 15.9155;
  const cx = 21;
  const cy = 21;
  const circumference = 2 * Math.PI * r; // ~100.02

  // Compute stroke-dashoffset for each segment
  let cumulativePercent = 0;
  const segments = categories.map((cat) => {
    const dashArray = (cat.percentage / 100) * circumference;
    const dashOffset =
      -(cumulativePercent / 100) * circumference + circumference * 0.25;
    cumulativePercent += cat.percentage;
    return { ...cat, dashArray, dashOffset };
  });

  const topCat = categories[0];

  return (
    <div className="flex flex-col sm:flex-row gap-7 items-center">
      {/* Donut SVG */}
      <div className="relative flex-shrink-0 w-[200px] h-[200px]">
        <svg viewBox="0 0 42 42" width="200" height="200">
          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="var(--surface-muted)"
            strokeWidth="6"
          />
          {/* Segments */}
          {segments.map((seg) => (
            <circle
              key={seg.category}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth="6"
              strokeDasharray={`${seg.dashArray} ${circumference - seg.dashArray}`}
              strokeDashoffset={seg.dashOffset}
              strokeLinecap="butt"
            />
          ))}
        </svg>
        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-[10px] tracking-[0.08em] text-muted-foreground">
              top category
            </div>
            <div className="text-[22px] font-bold tracking-tight mt-0.5 text-foreground">
              {topCat?.label.split(" ")[0]}
            </div>
            <div className="text-[11px] text-[var(--text-secondary)]">
              {topCat?.percentage}% · {topCat ? formatBaht(topCat.amount) : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <ul className="flex flex-col gap-2.5 flex-1 list-none p-0 m-0 min-w-0">
        {categories.map((cat) => (
          <li
            key={cat.category}
            className="grid items-center gap-2.5 grid-cols-[14px_1fr_auto_auto]"
          >
            <span
              className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0"
              style={{ background: cat.color }}
            />
            <span className="text-[13px] font-medium min-w-0 truncate text-foreground">
              {cat.label}
              <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                · {cat.transactionCount} tx
              </span>
            </span>
            <span className="text-[13px] font-bold tabular-nums text-foreground">
              {formatBaht(cat.amount)}
            </span>
            <span className="text-[11px] tabular-nums text-right w-9 text-muted-foreground">
              {cat.percentage}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
